import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AmorphicService } from '../amorphic/amorphic.service';
import { ApplicationService } from '../application/application.service';

import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { getStateChangeConfirmationModalConfig } from '../../components/modals/partials/generic/generic.configs';
import { ModalService } from '../modal/modal.service';
import { WorkflowService } from '../workflow/workflow.service';
import { Router } from '@angular/router';
import { Assumptions } from '../../../../common/js/static/Assumptions';
import { WindowService } from '../../../../common/angular-shared/services/window/window.service';

@Injectable({
  providedIn: 'root',
})
export class GeoChangeService {
  // Observable string source
  controller;
  private geoChangeSource = new Subject();
  private ownerTypeChangeSource = new Subject();
  stateChangeSource = new Subject<void>();
  // Observable string streams
  geoChangeSource$ = this.geoChangeSource.asObservable();
  ownerTypeChangeSource$ = this.ownerTypeChangeSource.asObservable();
  stateChangeSource$ = this.stateChangeSource.asObservable();

  constructor(
    private amorphicService: AmorphicService,
    private appSvc: ApplicationService,
    private modalService: ModalService,
    private workflowService: WorkflowService,
    private router: Router,
    protected windowService: WindowService
  ) {
    this.controller = amorphicService.controller;
  }

  public applyZipCodeResponse(modelString, zipcode, response) {
    this.appSvc.setValue(modelString + '.city', response['city']);
    this.appSvc.fireTrigger(modelString + '.city');
    this.appSvc.setValue(modelString + '.zip', zipcode);
    this.appSvc.fireTrigger(modelString + '.zip');
    this.appSvc.setValue(modelString + '.state', response['stateCode']);
    this.appSvc.fireTrigger(modelString + '.state');
    this.appSvc.setValue(modelString + '.latitude', response['lat']);
    this.appSvc.fireTrigger(modelString + '.latitude');
    this.appSvc.setValue(modelString + '.longitude', response['lng']);
    this.appSvc.fireTrigger(modelString + '.longitude');
    this.appSvc.setValue(modelString + '.timezone', response['timezone']);
    this.appSvc.fireTrigger(modelString + '.timezone');
  }

  zipCodeLookup(zipcode): Promise<any> {
    return new Promise((resolve, reject) => {
      this.controller
        .publicGetLocationByZip(zipcode)
        .then((response) => {
          if (!response) {
            return reject('Not supported');
          }
          return resolve(response);
        })
        .catch((reason) => {
          return reject(reason);
        });
    });
  }

  /**
   * Check whether application switch (metadata reload) is required due to state change.
   * A reload is required for following cases (not including ND, SD, or DE for NonICC states because they all have identical metadata as ICC):
   * ICC -> nonICC (only FL, CA, or NY),
   * nonICC (only FL, CA, or NY) -> ICC,
   * nonICC -> different nonICC (only if switching from FL, CA, or NY -> ND, SD, or DE ... or vice versa)
   *
   * @returns type of metadata switch. undefined if metadata switch not required
   */
  getMetadataSwitchEventIfRequired(prevState: string, newState: string): string {
    const prevICCStatus: boolean = CoverpathUtils.isICCState(prevState);
    const newICCStatus: boolean = CoverpathUtils.isICCState(newState);
    const uniqueNonICCStates: Array<string> = ['FL', 'CA', 'NY'];
    let eventLabel: string;

    if (prevICCStatus !== newICCStatus || (!prevICCStatus && prevState !== newState)) {
      /**
       * ICC -> Non ICC ... or vice versa
       * or
       * Non ICC -> Non ICC and state changed
       * */
      if (prevICCStatus !== newICCStatus) {
        // ICC -> Non ICC ... or vice versa
        if (prevICCStatus && uniqueNonICCStates.includes(newState)) {
          // ICC -> Non ICC that is "FL", "CA", or "NY"
          eventLabel = `ICC to Non-ICC (${newState})`;
        } else if (uniqueNonICCStates.includes(prevState)) {
          // Non ICC that is "FL", "CA", or "NY" -> ICC
          eventLabel = `Non-ICC (${prevState}) to ICC`;
        }
      } else if (uniqueNonICCStates.includes(prevState) || uniqueNonICCStates.includes(newState)) {
        // NonICC -> NonICC AND at least one of the states is "FL", "CA", or "NY"
        eventLabel = `Non-ICC (${prevState}) to another Non-ICC (${newState})`;
      }
    }
    return eventLabel;
  }

  /**
   * Display modal popup if the zip change requires a new application
   * Apply zip, city, state changes if no application switch is required
   *
   * @param modelString model path
   * @param newZipCode new zip code
   * @param oldZipCode old zip code
   * @param zipLookup lookup response of the new zip code
   */
  handleApplicationZipCodeChange(
    modelString: string,
    newZipCode: string,
    oldZipCode: string,
    eventPage: string,
    zipLookup: object
  ) {
    const address = this.appSvc.getValue(modelString);
    const oldState = address.state;
    const newState = zipLookup['stateCode'];
    const switchEventLabel = this.getMetadataSwitchEventIfRequired(oldState, newState);
    // application switch required?
    if (switchEventLabel) {
      const modalConfig = getStateChangeConfirmationModalConfig(
        () => this.handleStateChange(modelString, newZipCode, switchEventLabel, eventPage, zipLookup),
        () => this.handleStateRollback(modelString, oldZipCode)
      );
      this.appSvc.setValue(modelString + '.zip', oldZipCode);
      this.appSvc.fireTrigger(modelString + '.zip');
      // pop up a modal to have user confirm application change
      this.modalService.activate(modalConfig);
    }
    // does not require a new application
    else {
      const zipResponse = { prop: modelString, event: 'newZip', newZip: newZipCode, zipLookup: zipLookup };
      this.geoChangeSource.next(zipResponse);
    }
  }

  /**
   * Display modal popup if the zip change requires a new application.
   * Apply zip, city, state changes if no application switch is required.
   *
   * Won't update data model's zip with previous zipCode if app change is required.
   * This should be handled in the form's component via 'zipKept' subscription on geoChangeSource.
   * 'zipKept' will be triggered if the user selects no on the state change popup.
   *
   * @param modelString model path
   * @param oldState state address is changed from on form
   * @param newZipCode new zip code
   * @param oldZipCode old zip code
   * @param zipLookup lookup response of the new zip code
   */
  handleApplicationZipCodeChangeNonpersistent(
    modelString: string,
    oldState: string,
    newZipCode: string,
    oldZipCode: string,
    eventPage: string,
    zipLookup: object
  ) {
    const newState = zipLookup['stateCode'];
    const switchEventLabel = this.getMetadataSwitchEventIfRequired(oldState, newState);

    // application switch required?
    if (switchEventLabel) {
      const modalConfig = getStateChangeConfirmationModalConfig(
        () => this.handleStateChange(modelString, newZipCode, switchEventLabel, eventPage, zipLookup),
        () => this.handleStateRollback(modelString, oldZipCode)
      );
      // pop up a modal to have user confirm application change
      this.modalService.activate(modalConfig);
    }
    // does not require a new application
    else {
      const zipResponse = { prop: modelString, event: 'newZip', newZip: newZipCode, zipLookup: zipLookup };
      this.geoChangeSource.next(zipResponse);
    }
  }

  /**
   * Callback function when user confirms application switch
   */
  async handleStateChange(
    modelString: string,
    newZipCode: string,
    eventLabel: string,
    eventPage: string,
    zipLookup: object
  ) {
    // save ownerClassification and isJointOwnerSomeoneElse values
    const primaryPolicy = this.controller.customer.application.primaryPolicy;
    const ownerClassificationValue = primaryPolicy.ownerClassification;
    const insuredTypeValue = primaryPolicy.insuredType;
    const isJointOwnerSomeoneElseValue = primaryPolicy.isJointOwnerSomeoneElse;

    // cancel the application first if policy has already been submitted
    if (this.controller.isApplicationSubmitted()) {
      await this.workflowService.cancelApplication(primaryPolicy);
    }
    // reset the data and create a new policy from scratch
    await this.controller.customer.resetApplication(
      this.controller.customer.application,
      Assumptions.progress.ApplicationFill
    );

    // set 'ownerClassification' to what it was before application was reset
    if (ownerClassificationValue) {
      this.appSvc.setValue('customer.application.primaryPolicy.ownerClassification', ownerClassificationValue);
      this.appSvc.fireTrigger('customer.application.primaryPolicy.ownerClassification');
    }

    // set 'insuredType' to what it was before application was reset
    if (insuredTypeValue) {
      this.appSvc.setValue('customer.application.primaryPolicy.insuredType', insuredTypeValue);
      this.appSvc.fireTrigger('customer.application.primaryPolicy.insuredType');
    }

    /**
     * if insured = owner
     * OR
     * no 'ownerClassification' value
     * then need to set owner's state
     * owner needs a default state because we base the metadata off of the owner's state
     */
    if (!ownerClassificationValue || primaryPolicy.isOwnerSameAsInsured()) {
      const ownerAddressStateModelPath = 'customer.application.primaryPolicy.ownerPersonResidentialAddress.state';
      this.appSvc.setValue(ownerAddressStateModelPath, zipLookup['stateCode']);
      this.appSvc.fireTrigger(ownerAddressStateModelPath);
    }

    /**
     * If joint owner:
     *    Set 'isJointOwnerSomeoneElseValue' to what it was before application was reset
     *
     *    If the joint owner is someone else and the owner's state (not other owner) is changing
     *      OR
     *    If the insured's state is changing:
     *      Set other owner's state so it is the same as the owner/insured's state
     *
     *    If the joint owner is not someone else and an owner's state (the owner or other owner) is changing:
     *      Set insured's state so it is the same as the other owner's state
     *
     *    If other owner's state is changing or the insured's state is changing:
     *      Set owner's state so it is the same as the other owner's state
     */
    if (ownerClassificationValue === 'joint') {
      if (isJointOwnerSomeoneElseValue) {
        // set 'isJointOwnerSomeoneElseValue' to what it was before application was reset
        this.appSvc.setValue(
          'customer.application.primaryPolicy.isJointOwnerSomeoneElse',
          isJointOwnerSomeoneElseValue
        );
        this.appSvc.fireTrigger('customer.application.primaryPolicy.isJointOwnerSomeoneElse');
      }

      if (
        (isJointOwnerSomeoneElseValue === 'true' && modelString.indexOf('ownerPersonResidentialAddress') > 0) ||
        CoverpathUtils.isInsuredAddressProp(modelString)
      ) {
        // set other owner's state so it is the same as the owner/insured's state
        this.applyZipCodeResponse(
          'customer.application.primaryPolicy.otherOwner.residentialAddress',
          newZipCode,
          zipLookup
        );
      }

      if (isJointOwnerSomeoneElseValue !== 'true' && CoverpathUtils.isOwnerAddressProp(modelString)) {
        // set insured's state so it is the same as the other owner's state
        this.applyZipCodeResponse('customer.application.primaryPolicy.insured.address', newZipCode, zipLookup);
      }

      if (modelString.indexOf('otherOwner') > 0 || CoverpathUtils.isInsuredAddressProp(modelString)) {
        // set owner's state so it is the same as the other owner's state
        this.applyZipCodeResponse(
          'customer.application.primaryPolicy.ownerPersonResidentialAddress',
          newZipCode,
          zipLookup
        );
      }
    }

    // set the new zip for the new application
    this.applyZipCodeResponse(modelString, newZipCode, zipLookup);
    await this.controller.publicSave();

    // log GTM event
    CoverpathUtils.dataLayerPush({
      event: 'gtm-application--metadata--switch',
      eventCategory: 'Metadata Reload',
      eventAction: 'Switched app metadata',
      eventLabel: eventLabel,
      eventPage: eventPage,
    });
    if (this.router.url === '/application-form') {
      // reload application, and therefore rebuild app from new metadata
      // TODO HL-15056: Rebuild metadata without reloading the page
      this.windowService.nativeWindow.location.reload();
    } else {
      this.router.navigate(['application-form']);
    }
  }

  /**
   * Callback function when user cancels application switch
   */
  handleStateRollback(modelString: string, oldZipCode: string) {
    const zipResponse = { prop: modelString, event: 'zipKept' };
    if (oldZipCode === null) {
      oldZipCode = '';
    }
    this.geoChangeSource.next(zipResponse);
    this.modalService.deactivate();
  }

  /**
   * Display modal popup if the owner's state and "application" state requires a new application
   * Apply changes to the property which can cause an application change if no application switch is required
   * @param modelPath model path
   * @param insuredState insured's residential state
   * @param applicationState current "application" state (determines which metadata to display)
   * @param oldPropValue previous value of the property which can cause a application change
   * @param newPropValue new value of the property which can cause a application change
   * @param eventPage
   */
  changeBasedOnStateIfNecessary(
    modelPath: string,
    insuredState: string,
    applicationState: string,
    oldPropValue: string,
    newPropValue: string,
    eventPage: string
  ) {
    const switchEventLabel = this.getMetadataSwitchEventIfRequired(applicationState, insuredState);
    // application switch required?
    if (switchEventLabel) {
      const modalConfig = getStateChangeConfirmationModalConfig(
        () => this.handleStateChangeFromOwnerType(modelPath, newPropValue, switchEventLabel, eventPage),
        () => this.handleStateRollbackFromOwnerType(modelPath, oldPropValue)
      );

      // pop up a modal to have user confirm application change
      this.modalService.activate(modalConfig);
    } else {
      // set the new ownerClassification value
      this.submitOwnerResponse(modelPath, newPropValue, true);
    }
  }

  /**
   * Callback function when user confirms application switch
   * @param modelPath model path
   * @param newPropValue new value of the application change property
   * @param eventLabel
   * @param eventPage
   */
  async handleStateChangeFromOwnerType(modelPath: string, newPropValue: string, eventLabel: string, eventPage: string) {
    // cancel the application first if policy has already been submitted
    if (this.controller.isApplicationSubmitted()) {
      await this.workflowService.cancelApplication(this.controller.customer.application.primaryPolicy);
    }
    // reset the data and create a new policy from scratch
    await this.controller.customer.resetApplication(
      this.controller.customer.application,
      Assumptions.progress.ApplicationFill
    );

    // set the new value for the new application
    this.submitOwnerResponse(modelPath, newPropValue, true);

    // log GTM event
    CoverpathUtils.dataLayerPush({
      event: 'gtm-application--metadata--switch',
      eventCategory: 'Metadata Reload',
      eventAction: 'Switched app metadata',
      eventLabel: eventLabel,
      eventPage: eventPage,
    });
    if (this.router.url === '/application-form') {
      // reload application, and therefore rebuild app from new metadata
      // TODO HL-15056: Rebuild metadata without reloading the page
      this.windowService.nativeWindow.location.reload();
    } else {
      this.router.navigate(['application-form']);
    }
  }

  /**
   * Callback function when user confirms application switch
   * Also called when a state change modal is not necessary and we are setting the property value
   * @param modelPath model path
   * @param propValue new or old property value
   */
  submitOwnerResponse(modelPath: string, propValue: string, propChanged: boolean) {
    let eventStr: string;
    if (propChanged) {
      eventStr = 'propValueChanged';
    } else {
      eventStr = 'propValueKept';
    }
    const propResponse = { prop: modelPath, event: eventStr, value: propValue };
    this.geoChangeSource.next(propResponse);
    this.modalService.deactivate();
  }

  /**
   * Callback function when user cancels application switch (after switching Owner)
   * @param modelPath model path
   * @param oldPropValue previous prop value
   */
  async handleStateRollbackFromOwnerType(modelPath: string, oldPropValue: string) {
    this.submitOwnerResponse(modelPath, oldPropValue, false);
  }
}
